import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../api/apiClient';
import { authApi } from '../api/auth';

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function ProfileSettingsPage() {
  const { user, accessToken, logout } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState('');
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent]         = useState(false);
  const [showNew, setShowNew]                 = useState(false);
  const [showConfirm, setShowConfirm]         = useState(false);
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState('');
  const [success, setSuccess]                 = useState('');

  const [deletePassword, setDeletePassword]   = useState('');
  const [showDeletePw, setShowDeletePw]       = useState(false);
  const [deleting, setDeleting]               = useState(false);
  const [deleteError, setDeleteError]         = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Non-authenticated visitors — show sign-in / sign-up prompt (after all hooks)
  if (!user) {
    return (
      <div style={{ maxWidth: 480, margin: '60px auto', padding: '0 16px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>👤</div>
        <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 800, color: 'var(--text-primary)' }}>Profile</h1>
        <p style={{ margin: '0 0 32px', fontSize: '15px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Sign in to access your profile settings, change your password, and manage your account.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/login"    style={{ display: 'inline-flex', alignItems: 'center', height: '44px', padding: '0 26px', borderRadius: '12px', background: 'var(--primary-gradient)', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: '15px', boxShadow: '0 4px 14px rgba(99,102,241,0.35)' }}>
            Sign in
          </Link>
          <Link to="/register" style={{ display: 'inline-flex', alignItems: 'center', height: '44px', padding: '0 26px', borderRadius: '12px', background: 'var(--surface-strong)', border: '1.5px solid var(--primary)', color: 'var(--primary)', textDecoration: 'none', fontWeight: 700, fontSize: '15px' }}>
            Create account
          </Link>
        </div>
      </div>
    );
  }

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setSaving(true);
      const res = await apiFetch('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Request failed');
      }
      setSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Failed to change password.');
      return;
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');
    try {
      setDeleting(true);
      const res = await apiFetch('/api/auth/account', {
        method: 'DELETE',
        body: JSON.stringify({ password: deletePassword }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete account.');
      }
      logout();
      navigate('/');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete account.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={pageHeaderStyle}>
        <h1 style={pageTitleStyle}>Account Settings</h1>
        {user?.email && <p style={pageEmailStyle}>{user.email}</p>}
      </div>

      {/* ── Security ──────────────────────────────────── */}
      <section style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <h2 style={sectionTitleStyle}>Security</h2>
          <p style={sectionSubtitleStyle}>Update your password. We recommend using a strong, unique password.</p>
        </div>

        <form onSubmit={handleChangePassword} style={formStyle}>
          {error   && <div style={errorBoxStyle}>{error}</div>}
          {success && <div style={successBoxStyle}>{success}</div>}

          <div style={fieldsGridStyle}>
            {/* Current password */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Current password</label>
              <div style={inputWrapStyle}>
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  style={inputStyle}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
                <button type="button" style={eyeBtnStyle} onClick={() => setShowCurrent(v => !v)} tabIndex={-1}>
                  <EyeIcon open={showCurrent} />
                </button>
              </div>
            </div>

            {/* New password */}
            <div style={fieldStyle}>
              <label style={labelStyle}>New password</label>
              <div style={inputWrapStyle}>
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  style={inputStyle}
                  required
                  autoComplete="new-password"
                  placeholder="Min. 8 characters"
                />
                <button type="button" style={eyeBtnStyle} onClick={() => setShowNew(v => !v)} tabIndex={-1}>
                  <EyeIcon open={showNew} />
                </button>
              </div>
            </div>

            {/* Confirm new password */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Confirm new password</label>
              <div style={inputWrapStyle}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  style={inputStyle}
                  required
                  autoComplete="new-password"
                  placeholder="Repeat new password"
                />
                <button type="button" style={eyeBtnStyle} onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>
                  <EyeIcon open={showConfirm} />
                </button>
              </div>
            </div>
          </div>

          <div style={actionsStyle}>
            <button type="submit" style={saveButtonStyle} disabled={saving}>
              {saving ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </form>
      </section>

      {/* ── Your data (GDPR) ───────────────────────────── */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Your data</h2>
        <p style={sectionSubtitleStyle}>
          Inverow respects your right to data portability under GDPR. Download every piece of
          personal information we hold about you as a single JSON file. See our{' '}
          <Link to="/privacy" style={{ color: 'var(--accent)' }}>Privacy Policy</Link> for what we collect and why.
        </p>

        <div style={{ ...actionsStyle, marginTop: '12px' }}>
          <button
            type="button"
            onClick={async () => {
              if (exporting) return;
              setExporting(true);
              setExportMsg('');
              try {
                const fn = await authApi.dataExport(accessToken);
                setExportMsg(`Downloaded ${fn}`);
                setTimeout(() => setExportMsg(''), 5000);
              } catch (err) {
                setExportMsg(`Failed: ${err.message}`);
                setTimeout(() => setExportMsg(''), 6000);
              } finally {
                setExporting(false);
              }
            }}
            disabled={exporting}
            style={{ ...saveButtonStyle, opacity: exporting ? 0.6 : 1 }}
          >
            {exporting ? 'Preparing…' : 'Download my data (.json)'}
          </button>
          {exportMsg && <span style={{ marginLeft: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>{exportMsg}</span>}
        </div>
      </section>

      {/* ── Danger Zone ───────────────────────────────── */}
      <section style={dangerSectionStyle}>
        <div style={dangerHeaderStyle}>
          <div>
            <h2 style={dangerTitleStyle}>Danger Zone</h2>
            <p style={dangerSubtitleStyle}>
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
          </div>
          {!showDeleteConfirm && (
            <button
              type="button"
              onClick={() => { setShowDeleteConfirm(true); setDeleteError(''); }}
              style={deleteButtonStyle}
            >
              Delete account
            </button>
          )}
        </div>

        {showDeleteConfirm && (
          <form onSubmit={handleDeleteAccount} style={deleteFormStyle}>
            <p style={deleteWarningStyle}>
              Enter your current password to confirm account deletion. All portfolios, transactions, and data will be permanently removed.
            </p>

            {deleteError && <div style={deleteErrorBoxStyle}>{deleteError}</div>}

            <div style={fieldStyle}>
              <label style={labelStyle}>Confirm with your password</label>
              <div style={inputWrapStyle}>
                <input
                  type={showDeletePw ? 'text' : 'password'}
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  style={inputStyle}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
                <button type="button" style={eyeBtnStyle} onClick={() => setShowDeletePw(v => !v)} tabIndex={-1}>
                  <EyeIcon open={showDeletePw} />
                </button>
              </div>
            </div>

            <div style={deleteActionsStyle}>
              <button
                type="button"
                onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); setDeleteError(''); }}
                style={cancelButtonStyle}
                disabled={deleting}
              >
                Cancel
              </button>
              <button type="submit" style={confirmDeleteButtonStyle} disabled={deleting || !deletePassword}>
                {deleting ? 'Deleting…' : 'Permanently delete account'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

/* ── Styles ── */
const pageStyle = {
  maxWidth: '720px',
  width: '100%',
  margin: '0 auto',
  padding: '20px 0 60px',
  boxSizing: 'border-box',
  display: 'grid',
  gap: '24px',
  alignContent: 'start',
};

const pageHeaderStyle = {
  display: 'grid',
  gap: '4px',
};

const pageTitleStyle = {
  margin: 0,
  color: 'var(--text-primary)',
  fontSize: '28px',
  fontWeight: 800,
};

const pageEmailStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
  fontSize: '15px',
};

const sectionStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
  display: 'grid',
  gap: '20px',
};

const sectionHeaderStyle = {
  display: 'grid',
  gap: '4px',
};

const sectionTitleStyle = {
  margin: 0,
  color: 'var(--text-primary)',
  fontSize: '18px',
  fontWeight: 800,
};

const sectionSubtitleStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
  fontSize: '14px',
  lineHeight: 1.5,
};

const formStyle = {
  display: 'grid',
  gap: '16px',
};

const fieldsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
  gap: '16px',
};

const fieldStyle = {
  display: 'grid',
  gap: '6px',
};

const labelStyle = {
  fontSize: '12px',
  fontWeight: 700,
  color: 'var(--text-secondary)',
};

const inputWrapStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const inputStyle = {
  width: '100%',
  minHeight: '48px',
  borderRadius: '12px',
  border: '1px solid var(--border)',
  background: 'var(--input-bg)',
  color: 'var(--text-primary)',
  padding: '12px 44px 12px 14px',
  fontSize: '14px',
  boxSizing: 'border-box',
};

const eyeBtnStyle = {
  position: 'absolute',
  right: '12px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-secondary)',
  display: 'flex',
  alignItems: 'center',
  padding: '4px',
};

const actionsStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
};

const saveButtonStyle = {
  border: 'none',
  borderRadius: '12px',
  padding: '12px 28px',
  fontWeight: 700,
  fontSize: '15px',
  cursor: 'pointer',
  background: 'var(--primary)',
  color: '#ffffff',
};

const errorBoxStyle = {
  padding: '12px 16px',
  borderRadius: '12px',
  background: 'rgba(220, 38, 38, 0.12)',
  color: '#fca5a5',
  border: '1px solid rgba(220, 38, 38, 0.35)',
  fontWeight: 600,
  fontSize: '14px',
};

const successBoxStyle = {
  padding: '12px 16px',
  borderRadius: '12px',
  background: 'rgba(34, 197, 94, 0.12)',
  color: '#86efac',
  border: '1px solid rgba(34, 197, 94, 0.35)',
  fontWeight: 600,
  fontSize: '14px',
};

const dangerSectionStyle = {
  borderRadius: '16px',
  padding: '20px 24px',
  border: '1px solid rgba(220, 38, 38, 0.35)',
  background: 'rgba(220, 38, 38, 0.08)',
  display: 'grid',
  gap: '16px',
};

const dangerHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  flexWrap: 'wrap',
};

const dangerTitleStyle = {
  margin: 0,
  color: '#fca5a5',
  fontSize: '18px',
  fontWeight: 800,
};

const dangerSubtitleStyle = {
  margin: '6px 0 0 0',
  color: '#fecaca',
  fontSize: '14px',
  lineHeight: 1.5,
};

const deleteButtonStyle = {
  border: 'none',
  borderRadius: '12px',
  padding: '12px 20px',
  fontWeight: 700,
  cursor: 'pointer',
  background: '#dc2626',
  color: '#ffffff',
  flexShrink: 0,
  fontSize: '14px',
};

const deleteFormStyle = {
  display: 'grid',
  gap: '14px',
};

const deleteWarningStyle = {
  margin: 0,
  color: '#fecaca',
  fontSize: '14px',
  lineHeight: 1.55,
};

const deleteErrorBoxStyle = {
  padding: '12px 16px',
  borderRadius: '12px',
  background: 'rgba(220, 38, 38, 0.18)',
  color: '#fca5a5',
  border: '1px solid rgba(220, 38, 38, 0.45)',
  fontWeight: 600,
  fontSize: '14px',
};

const deleteActionsStyle = {
  display: 'flex',
  gap: '10px',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
};

const cancelButtonStyle = {
  border: '1px solid rgba(220, 38, 38, 0.4)',
  borderRadius: '12px',
  padding: '12px 20px',
  fontWeight: 700,
  cursor: 'pointer',
  background: 'transparent',
  color: '#fca5a5',
  fontSize: '14px',
};

const confirmDeleteButtonStyle = {
  border: 'none',
  borderRadius: '12px',
  padding: '12px 20px',
  fontWeight: 700,
  cursor: 'pointer',
  background: '#dc2626',
  color: '#ffffff',
  fontSize: '14px',
};

export default ProfileSettingsPage;
